/**
 * useRecentPrompts — automatic history of last N generated prompts.
 * Different from usePromptsLibrary: this is auto-recorded + capped, not curated.
 * Use case: "co ja generowałem 5 minut temu, chcę szybko wrócić".
 */
import { useCallback, useEffect, useState } from "react";

export interface RecentPrompt {
  text: string;
  usedAt: string;
}

const STORAGE_KEY = "viralpartners.recentPrompts.v1";
const MAX_ENTRIES = 10;

function loadFromStorage(): RecentPrompt[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is RecentPrompt =>
        typeof p === "object" &&
        p !== null &&
        typeof (p as RecentPrompt).text === "string" &&
        typeof (p as RecentPrompt).usedAt === "string"
    );
  } catch {
    return [];
  }
}

function saveToStorage(prompts: RecentPrompt[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prompts));
}

export function useRecentPrompts() {
  const [recent, setRecent] = useState<RecentPrompt[]>(() => loadFromStorage());

  useEffect(() => {
    saveToStorage(recent);
  }, [recent]);

  const recordUsage = useCallback((text: string): void => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setRecent((prev) => {
      const without = prev.filter((p) => p.text !== trimmed); // de-dupe & promote to top
      return [{ text: trimmed, usedAt: new Date().toISOString() }, ...without].slice(
        0,
        MAX_ENTRIES
      );
    });
  }, []);

  const clearRecent = useCallback((): void => {
    setRecent([]);
  }, []);

  return { recent, recordUsage, clearRecent };
}
